<template>
  <transition name="fade" enter-active-class="afdc-animated afdc-animated-fast afdc-slideInLeft" leave-active-class="afdc-animated afdc-animated-fast afdc-slideOutLeft" v-on:after-enter="afterEnter">
    <div id="afdc-alt-fuel-station" :class="{'afdc-mobile-station-show': isMobileView}" tabindex="0">
      <div id="afdc-close-station" class="afdc-float-right" v-on:click="close">
        <span class="afdc-sr-only">{{ $t('Close') }}</span>
        <font-awesome-icon icon="times-circle" class="afdc-fa-icon"/>
      </div>

      <div class="afdc-float-right afdc-kiosk-mode-hide" v-if="station_data.id">
        <router-link v-bind:to="{ name: 'StationEdit', params: { id: station_data.id } }" class="afdc-station-edit-link">
          <small>{{ $t('Report a change') }}</small>
        </router-link>
      </div>

      <div v-if="$store.getters.getLoadingStationDetails" id="afdc-station-spinner-container">
        <div id="afdc-station-spinner"></div>
      </div>

      <template v-if="$store.getters.getStationError">
        <p class="afdc-alert afdc-alert-warning afdc-station-error"><em>{{ $store.getters.getStationError }}</em></p>
      </template>

      <div class="afdc-row afdc-clearboth" v-if="!$store.getters.getStationError && station_data.id">
        <div class="afdc-col-sm-7">
          <h2>{{ station_data.station_name }}</h2>

          <br>
          <div class="afdc-row">
            <div class="afdc-col-lg-6">
              <p>
                {{ station_data.street_address }}
                <br>
                {{ stationCityStateZip(station_data.city, station_data.state, station_data.zip) }}
              </p>
            </div>

            <div class="afdc-col-lg-6">
              <template v-if="station_data.latitude && station_data.longitude">
                <a v-bind:href="stationDirections(station_data.latitude, station_data.longitude, station_data.street_address, stationCityStateZip(station_data.city, station_data.state, station_data.zip))" class="afdc-btn afdc-btn-success afdc-directions-button afdc-kiosk-mode-hide" target="_blank" rel="noopener noreferrer">{{ $t('Directions') }}</a>
              </template>
            </div>
          </div>

          <p v-if="localeIntsectionDirections"><span v-html="localeIntsectionDirections.replace(/(?:\r\n|\r|\n)/g, '<br>')"></span></p>

          <p v-if="station_data.station_phone">
            <a v-for="phone in stationPhoneNumbers(station_data.station_phone)" v-bind:key="phone" v-bind:href="'tel:' + phone" itemprop="telephone" class="afdc-phone-link"><font-awesome-icon icon="phone" class="afdc-fa-icon"/>{{ phone }}</a>
            <br class="afdc-clear">
          </p>

          <p v-if="station_data.maximum_vehicle_class">
            <strong>{{ $t('Vehicle Accessibility:') }}</strong>
            {{ vehicleAccessibility(station_data.maximum_vehicle_class) }}
          </p>

          <template v-if="station_data.fuel_type_code === 'BD'">
            <h3>{{ $t('Biodiesel') }}</h3>
            <p v-if="localeBdBlends"><strong>{{ $t('Blends Available:') }}</strong> {{ localeBdBlends }}</p>
          </template>

          <template v-if="station_data.fuel_type_code === 'CNG'">
            <h3>{{ $t('Compressed Natural Gas (CNG)') }}</h3>
            <p v-if="station_data.cng_has_rng">{{ $t('This station sells renewable natural gas (RNG).') }}</p>
            <p v-if="station_data.ng_fill_type_code"><strong>{{ $t('Fill Type:') }}</strong> {{ cngFillType(station_data.ng_fill_type_code) }}</p>
            <p v-if="station_data.ng_psi"><strong>{{ $t('Fill Pressure:') }}</strong> {{ cngPsis(station_data.ng_psi) }}</p>
          </template>

          <template v-if="station_data.fuel_type_code === 'ELEC'">
            <h3>{{ $t('Electric Vehicle Charging') }}</h3>

            <template v-if="station_data.ev_connector_types">
              <div class="afdc-station-port-connector"><strong>{{ $t('Connectors:') }}</strong></div>
              <template v-for="connector in station_data.ev_connector_types">
                <div v-bind:key="connector" class="afdc-station-port-connector">
                  <img v-bind:src="require(`@/assets/images/${stationEvConnectors(connector, 'image') }.png`)">
                  {{ stationEvConnectors(connector, 'label') }}
                </div>
              </template>
            </template>

            <table cellpadding="4" cellspacing="0" width="100%" class="afdc-data" border="0">
              <thead>
                <tr class="afdc-background-gray-light">
                  <th width="40%" id="afdc-type">{{ $t('Type') }}</th>
                  <th width="60%" id="afdc-ports">{{ $t('EV Charging Ports') }} <a href="#" v-on:click.prevent="toggleEvTerms" id="afdc-ev-terms-link"><font-awesome-icon icon="question-circle" class="afdc-fa-icon"/></a></th>
                </tr>
              </thead>
              <tbody>
                <template v-for="level in stationEvData(station_data.ev_level1_evse_num, station_data.ev_level2_evse_num, station_data.ev_dc_fast_num)">
                  <tr v-if="level.number" v-bind:key="level.text">
                    <td headers="afdc-type">{{ level.text }}</td>
                    <td headers="afdc-ports">{{ level.number }}</td>
                  </tr>
                </template>
              </tbody>
            </table>

            <p v-if="station_data.ev_network">
              <strong>{{ $t('Charging Network:') }}</strong>
              <template v-if="station_data.ev_network_web">
                <a v-bind:href="station_data.ev_network_web" target="_blank" rel="noopener noreferrer">{{ evNetwork(station_data.ev_network) }}</a>
              </template>
              <template v-else>
                {{ evNetwork(station_data.ev_network) }}
              </template>
            </p>

            <div v-if="station_data.ev_pricing">
              <p><strong>{{ $t('Charging and/or Parking Cost:') }}</strong> {{ localeEvPricing }}</p>
            </div>
          </template>

          <template v-if="station_data.fuel_type_code === 'E85'">
            <h3>{{ $t('Ethanol') }}</h3>
            <p><strong>{{ $t('Mid-Level Blends:') }}</strong> {{ station_data.e85_blender_pump ? $t('Yes') : $t('No') }}</p>

            <p v-if="station_data.e85_other_ethanol_blends">
              <strong>{{ $t('Other Blends Offered:') }}</strong>
              <template v-for="(blend, index) in station_data.e85_other_ethanol_blends">
                <span v-bind:key="blend">{{ blend }}<span v-if="index + 1 < station_data.e85_other_ethanol_blends.length">, </span></span>
              </template>
            </p>
          </template>

          <template v-if="station_data.fuel_type_code === 'HY'">
            <h3>{{ $t('Hydrogen') }}</h3>
            <p v-if="station_data.hy_status_link" class="afdc-kiosk-mode-hide"><a v-bind:href="station_data.hy_status_link" target="_blank" rel="noopener noreferrer">{{ $t('Station status') }}</a></p>

            <p v-if="!station_data.hy_is_retail">{{ $t('This station is only available to a certain subset of customers and is considered a non-retail location.') }}</p>

            <p v-else>{{ $t('This station is available to all customers and is considered a retail location.') }}</p>

            <p v-if="station_data.hy_pressures">
              <strong>{{ $t('Pressure Available:') }}</strong>
              <template v-for="(pressure, index) in station_data.hy_pressures">
                <span v-bind:key="pressure">{{ $sprintf($t('%s bar'), pressure) }}<span v-if="index + 1 < station_data.hy_pressures.length">, </span></span>
              </template>
            </p>

            <p v-if="station_data.hy_standards">
              <strong>{{ $t('SAE Standards Compliance:') }}</strong>
              <template v-for="(standard, index) in station_data.hy_standards">
                <span v-bind:key="standard">{{ standard }}<span v-if="index + 1 < station_data.hy_standards.length">, </span></span>
              </template>
            </p>
          </template>

          <template v-if="station_data.fuel_type_code === 'LNG'">
            <h3>{{ $t('Liquefied Natural Gas (LNG)') }}</h3>
            <p v-if="station_data.lng_has_rng">{{ $t('This station sells renewable natural gas (RNG).') }}</p>
          </template>

          <template v-if="station_data.fuel_type_code === 'LPG'">
            <h3>{{ $t('Propane (Liquefied Petroleum Gas [LPG])') }}</h3>
            <p v-if="station_data.lpg_primary">{{ $t('This station has fuel for vehicles and vehicle-specific fueling services that are consistently offered during business hours.') }}</p>

            <p v-else>{{ $t('This station has fuel for vehicles but limited availability and/or limited vehicle-specific fueling capabilities.') }}</p>

            <p v-if="station_data.lpg_nozzle_types">
              <strong>{{ $t('Propane Nozzles:') }}</strong>
              <template v-for="(nozzle, index) in station_data.lpg_nozzle_types">
                <span v-bind:key="nozzle">{{ lpgNozzleTypes(nozzle) }}<span v-if="index + 1 < station_data.lpg_nozzle_types.length">, </span></span>
              </template>
            </p>
          </template>

          <template v-if="station_data.fuel_type_code === 'RD'">
            <h3>{{ $t('Renewable Diesel') }}</h3>
            <p v-if="localeRdBlends"><strong>{{ $t('Blends Available:') }}</strong> {{ localeRdBlends }}</p>

            <p v-if="station_data.rd_blended_with_biodiesel"><strong>{{ $t('Blended with Biodiesel:') }}</strong> {{ station_data.rd_blended_with_biodiesel === 'Y' ? $t('Yes') : $t('No') }}</p>

            <p v-if="station_data.rd_blended_with_biodiesel && station_data.rd_blended_with_biodiesel === 'Y' && station_data.rd_max_biodiesel_level"><strong>{{ $t('Maximum Biodiesel Level:') }}</strong> {{ station_data.rd_max_biodiesel_level }}</p>
          </template>
        </div>

        <div class="afdc-col-sm-5">
          <station-show-small-map/>

          <div class="afdc-row">
            <div class="afdc-col-md-3">
              <font-awesome-icon icon="key" class="afdc-fa-icon afdc-station-details-icon"/>
            </div>
            <div class="afdc-col-md-9">
              <p>{{ localeGroupsWithAccessCode }}</p>
            </div>
          </div>

          <template v-if="localeAccessDaysTime">
            <br>
            <div class="afdc-row">
              <div class="afdc-col-md-3">
                <font-awesome-icon icon="clock" class="afdc-fa-icon afdc-station-details-icon"/>
              </div>
              <div class="afdc-col-md-9">
                <p>{{ localeAccessDaysTime }}</p>
              </div>
            </div>
          </template>

          <template v-if="station_data.cards_accepted">
            <br>
            <div class="afdc-row">
              <div class="afdc-col-md-3">
                <font-awesome-icon icon="credit-card" class="afdc-fa-icon afdc-station-details-icon"/>
              </div>
              <div class="afdc-col-md-9">
                <p>{{ paymentMethods(station_data.cards_accepted) }}</p>
              </div>
            </div>
          </template>
        </div>
      </div>

      <br>
      <p v-if="station_data.date_last_confirmed">{{ $sprintf($t('Last confirmed: %s'), $formatDate(station_data.date_last_confirmed, { month: 'long', year: 'numeric' })) }}</p>

      <p><em>{{ $t('Station details are subject to change. We recommend calling the station to verify location, hours of operation, and access.') }}</em></p>
    </div>
  </transition>
</template>

<script>
import StationShowSmallMap from '@/components/StationShowSmallMap.vue';

export default {
  components: {
    StationShowSmallMap,
  },

  computed: {
    station_data() {
      return this.$store.getters.stationDetails;
    },

    localeEvPricing() {
      return this.fieldInLocale('ev_pricing');
    },

    localeAccessDaysTime() {
      return this.fieldInLocale('access_days_time');
    },

    localeBdBlends() {
      return this.fieldInLocale('bd_blends');
    },

    localeRdBlends() {
      return this.fieldInLocale('rd_blends');
    },

    localeGroupsWithAccessCode() {
      return this.fieldInLocale('groups_with_access_code');
    },

    localeIntsectionDirections() {
      return this.fieldInLocale('intersection_directions');
    },

    isMobileView() {
      return this.$store.getters.isMobileView;
    },
  },

  watch: {
    $route: {
      immediate: true,
      handler: 'fetchData',
    },

    station_data: 'dataUpdated',

    '$route.query.show_ev_terms': {
      immediate: true,
      handler: 'setEvTermsModalOpen',
    },
  },

  methods: {
    afterEnter() {
      this.focusOnLoad();
    },

    fetchData() {
      if (this.$route.name === 'StationShow') {
        this.$store.dispatch('fetchStationDetails', this.$route.params.id);
      }
    },

    focusOnLoad() {
      if (this.$el) {
        this.$el.focus();
      }
    },

    dataUpdated() {
      this.focusOnLoad();
    },

    close() {
      // To close the stations view, navigate back to the previously active
      // route (if one exists), taking into account any existing filters (so
      // their current search is retained).
      this.$router.push({
        name: this.$store.getters.getNavActiveFilterRouteName,
        query: this.$store.getters.getFilters,
      });
    },

    fieldInLocale(field) {
      const fieldFr = `${field}_fr`;
      if (this.$store.getters.localeLang === 'fr' && (this.station_data[fieldFr] || this.$store.getters.localeDisableFallbackCountries.includes(this.station_data.country))) {
        return this.station_data[fieldFr];
      }
      return this.station_data[field];
    },

    paymentMethods(codes) {
      const methods = this.$store.getters.loaderPaymentMethodNamesByKey;
      return codes.split(' ').map((payment) => methods[payment]).join(', ');
    },

    evNetwork(code) {
      return code === 'Non-Networked' ? this.$t('None') : this.$store.getters.loaderElecNetworksByKey[code];
    },

    cngFillType(code) {
      return code === 'B' ? this.$t('Fast-fill and time-fill') : this.$store.getters.loaderCngFillTypesByKey[code];
    },

    cngPsis(codes) {
      const psis = this.$store.getters.loaderCngPsisByKey;
      return codes.split(' ').map((psi) => psis[psi]).join(', ');
    },

    vehicleAccessibility(code) {
      switch (code) {
        case 'LD':
          return this.$t('Accommodates light-duty vehicles only (i.e., passenger vehicles, light-duty pickups, and utility vans)');
        case 'MD':
          return this.$t('Accommodates vehicles up to Class 5 (i.e., box trucks and cargo vans)');
        case 'HD':
          return this.$t('Accommodates all vehicle sizes and classes');
        default:
          return null;
      }
    },

    lpgNozzleTypes(code) {
      return this.$store.getters.loaderLpgNozzleTypeNamesByKey[code];
    },

    setEvTermsModalOpen() {
      if (this.$route.query.show_ev_terms === 'true') {
        this.$store.commit('setEvTermsModalOpen', true);
      } else {
        this.$store.commit('setEvTermsModalOpen', false);
      }
    },

    toggleEvTerms() {
      this.$store.commit('setEvTermsModalLinkClicked', true);
      if (this.$store.getters.getEvTermsModalOpen) {
        this.$router.push({ query: { ...this.$route.query, show_ev_terms: undefined } });
      } else {
        this.$router.push({ query: { ...this.$route.query, show_ev_terms: 'true' } });
      }
    },
  },
};
</script>

<style lang="scss">
#alt-fuel-station {
  scroll-margin-top: 45px;
  .station-edit-link {
    margin-right: 15px;
  }

  #ev-terms-link .fa-icon {
    color: #333;
    &:hover {
      color: #666;
    }
  }
}
</style>
